import { Select, Spin, message, Divider, Typography, Alert } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Form,
  FormItem,
  SelectItem,
  InputItem,
  InputNumberItem,
  ButtonItem,
  SwitchItem,
  ErrorItem,
} from "../../../../Components/Items";

const { Option } = Select;
const { Title } = Typography;

// Criterios de la exención del ISR por salario mínimo (Art. 96 LISR). Las etiquetas son las
// mismas que usa el backend (Utils/exencionSalarioMinimo.utils.js) en la explicación del ISR
// del recibo, para que el usuario reconozca la opción en los dos lados.
const CRITERIOS_EXENCION_SM = [
  {
    valor: "diasPeriodo",
    etiqueta: "Base del periodo vs. salario mínimo × días",
    regla: "No se le retiene ISR al empleado si la base gravable del periodo no pasa el salario mínimo diario multiplicado por los días del periodo.",
    falta: "Si falta un día, su base baja. Un empleado que gana un poco más del mínimo puede quedar debajo del tope y no se le retiene ISR esa semana.",
    bono: "Si gana el mínimo pero trae un bono u otra percepción gravada, la base pasa el tope y sí se le retiene.",
    nota: "Es el criterio de CONTPAQi / NOMIPAQ y el que se usaba antes de que existiera esta opción.",
  },
  {
    valor: "salarioDiario",
    etiqueta: "Salario diario vs. salario mínimo",
    regla: "No se le retiene ISR al empleado si su salario diario no pasa el salario mínimo diario. Se compara directamente, sin multiplicar por los días del periodo.",
    falta: "Las faltas no cambian nada: si su salario diario pasa el mínimo, se le retiene aunque haya faltado.",
    bono: "Si gana el mínimo, no se le retiene aunque traiga un bono u otra percepción gravada.",
  },
  {
    valor: "salarioYBase",
    etiqueta: "Salario diario y base del periodo",
    regla: "No se le retiene ISR al empleado sólo si se cumplen las dos condiciones: su salario diario no pasa el salario mínimo diario Y la base del periodo no pasa el salario mínimo × días.",
    falta: "Si gana más del mínimo, se le retiene aunque haya faltado, porque su salario pasa el mínimo.",
    bono: "Si gana el mínimo pero trae un bono u otra percepción gravada, la base pasa el tope y sí se le retiene.",
  },
];

class ConfiguracionNominaContabilidad extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    tiposDePolizas: [],
    proyectos: [],
  };

  componentDidMount() {
    this.consultarDatos();
  }

  consultarDatos() {
    this.setState({ loading: true });

    axios
      .post(
        "empresas/selectConfiguracionNomina",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          const c = res.data.configuracionNomina || {};
          this.formRef.current?.setFieldsValue({
            tipoDePolizaId: c.tipoDePolizaId,
            proyectoId: c.proyectoId,
            polizaPorEmpleado: !!c.polizaPorEmpleado,
            desactivarMovimientosObligaciones: !!c.desactivarMovimientosObligaciones,
            netearSubsEntregado: !!c.netearSubsEntregado,
            metodoISR: c.metodoISR || "SP",
            // Una empresa que nunca lo configuró no trae el campo: se queda en el criterio de siempre.
            criterioExencionSalarioMinimo: c.criterioExencionSalarioMinimo || "diasPeriodo",
            ajustarISRRetenidoMenos: c.ajustarISRRetenidoMenos !== false,
            ajustarISRRetenidoMas: c.ajustarISRRetenidoMas !== false,
            ajustarSubsCausadoMenos: !!c.ajustarSubsCausadoMenos,
            aplicarSubsidioArt174: !!c.aplicarSubsidioArt174,
            educacion1Pct: !!c.educacion1Pct,
            multiploAjusteNeto: c.multiploAjusteNeto ?? 0.2,
            preferenciaAjusteNetoNegativo: c.preferenciaAjusteNetoNegativo !== false,
            primaRiesgoTrabajo: c.primaRiesgoTrabajo ?? 0.005,
            zonaMinimo: c.zonaMinimo || "A",
            diasAnioAguinaldo: c.diasAnioAguinaldo ?? 365,
            diasAnioVacaciones: c.diasAnioVacaciones ?? 365,
            // Una empresa que nunca los configuró no trae los campos: se queda en "cumplidos + 1".
            antiguedadCumplidosSbc: c.antiguedadCumplidosSbc === true,
            antiguedadCumplidosAguinaldo: c.antiguedadCumplidosAguinaldo === true,
            antiguedadCumplidosVacacionesFiniquito: c.antiguedadCumplidosVacacionesFiniquito === true,
            centroTrabajoFonacot: c.centroTrabajoFonacot || "",
            numeroFonacot: c.numeroFonacot || "",
          });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Error al cargar la configuración de nómina");
      });

    axios
      .post(
        "tiposDePolizas/selectTiposDePolizas",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status === 404) return;
        this.setState({ tiposDePolizas: res.data });
      });

    axios
      .post(
        "proyectos/selectProyectos",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status === 404) return;
        this.setState({ proyectos: res.data });
      });
  }

  onFinishFailed(e) { ErrorItem(e); }

  onFinish(values) {
    this.setState({ loading: true });
    axios
      .post(
        "empresas/editarConfiguracionNomina",
        { ...values, empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          message.success("Configuración de nómina actualizada");
        } else {
          message.error(res.data.message || "No se pudo actualizar la configuración");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  render() {
    const { tiposDePolizas, proyectos } = this.state;

    return (
      <Spin spinning={this.state.loading}>
      <div style={{ maxWidth: 640 }}>
        <Form
          ref={this.formRef}
          name="formConfiguracionNominaContabilidad"
          layout
          bottom={
            <ButtonItem type="primary" htmlType="submit"
              style={{ display: "block", margin: "0 0 0 auto" }}
              loading={this.state.loading}>
              Guardar
            </ButtonItem>
          }
          onFinish={this.onFinish.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <Title level={5}>Póliza Contable</Title>

          <FormItem
            name="tipoDePolizaId"
            label="Tipo de Póliza"
            margin
            required
            tooltip="Tipo de póliza que se usará para todas las pólizas de nómina."
          >
            <SelectItem placeholder="Selecciona el tipo de póliza" showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {tiposDePolizas.map((t) => (
                <Option value={t._id} key={t._id}>{t.nombre}</Option>
              ))}
            </SelectItem>
          </FormItem>

          <FormItem
            name="proyectoId"
            label="Proyecto General"
            margin
            required
            tooltip="Proyecto donde se registra el lado de pasivo de la póliza de nómina; el lado de costo/gasto usa el proyecto del departamento del empleado cuando lo tiene."
          >
            <SelectItem placeholder="Selecciona el proyecto general" showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {proyectos.map((p) => (
                <Option value={p._id} key={p._id}>{p.nombre}</Option>
              ))}
            </SelectItem>
          </FormItem>

          <FormItem
            name="polizaPorEmpleado"
            label="Generar una póliza por empleado"
            margin
            valuePropName="checked"
            tooltip="Si está apagado, se genera una sola póliza general por nómina. Si está encendido, se genera una póliza independiente por cada empleado."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="desactivarMovimientosObligaciones"
            label="Desactivar movimientos de las obligaciones"
            margin
            valuePropName="checked"
            tooltip="Si está encendido, la póliza de nómina ya no contabiliza el pasivo ni el gasto de los conceptos de tipo Obligación (IMSS, INFONAVIT, etc.). Percepciones y deducciones no se ven afectadas."
          >
            <SwitchItem />
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>Cálculo de ISR</Title>

          <FormItem
            name="metodoISR"
            label="Método de cálculo de ISR"
            margin
            tooltip="Procedimiento (SP): proyecta el ISR directamente sobre el periodo. Mensualizado: acumula y ajusta el ISR mes a mes antes de prorratearlo al periodo."
          >
            <SelectItem>
              <Option value="SP">Procedimiento (SP)</Option>
              <Option value="mensualizado">Mensualizado</Option>
            </SelectItem>
          </FormItem>

          <FormItem
            name="criterioExencionSalarioMinimo"
            label="Exención por salario mínimo (Art. 96)"
            margin
            tooltip="A quien gana el salario mínimo no se le retiene ISR. Aquí eliges cómo se decide si el empleado gana el mínimo. Abajo se explica cada opción."
          >
            <SelectItem>
              {CRITERIOS_EXENCION_SM.map((c) => (
                <Option value={c.valor} key={c.valor}>{c.etiqueta}</Option>
              ))}
            </SelectItem>
          </FormItem>

          <FormItem noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const elegido = getFieldValue("criterioExencionSalarioMinimo") || "diasPeriodo";
              const criterio = CRITERIOS_EXENCION_SM.find((c) => c.valor === elegido) || CRITERIOS_EXENCION_SM[0];
              const mensualizado = getFieldValue("metodoISR") === "mensualizado";
              return (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16, fontSize: 12 }}
                  message={<b>{criterio.etiqueta}</b>}
                  description={
                    <div>
                      <p style={{ margin: "0 0 6px" }}>{criterio.regla}</p>
                      <ul style={{ margin: "0 0 6px", paddingLeft: 18 }}>
                        <li><b>Si falta un día:</b> {criterio.falta}</li>
                        <li><b>Si trae un bono:</b> {criterio.bono}</li>
                      </ul>
                      {criterio.nota && <p style={{ margin: "0 0 6px" }}>{criterio.nota}</p>}
                      <p style={{ margin: "0 0 6px" }}>
                        En el cierre de mes se usa el mismo criterio. Así el ajuste mensual no cobra
                        ni devuelve ISR por una exención que se decidió distinto en el periodo.
                      </p>
                      <p style={{ margin: "0 0 6px" }}>
                        Si el empleado no tiene salario diario capturado (por ejemplo, un asimilado),
                        se evalúa con la base del periodo contra el salario mínimo × días.
                      </p>
                      <p style={{ margin: 0 }}>
                        En cada recibo, el botón de ISR del sobre-recibo muestra qué comparación se hizo
                        y cuál decidió.
                        {mensualizado && (
                          <b> Con el método Mensualizado, esta exención no se evalúa en el periodo;
                          sólo aplica en el cierre de mes.</b>
                        )}
                      </p>
                    </div>
                  }
                />
              );
            }}
          </FormItem>

          <FormItem
            name="ajustarISRRetenidoMenos"
            label="Ajustar ISR retenido de menos"
            margin
            valuePropName="checked"
            tooltip="Al cierre de mes, reintegra al empleado el ISR que se le retuvo de menos durante el mes. Si está apagado, ese ajuste nunca se aplica."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="ajustarISRRetenidoMas"
            label="Ajustar ISR retenido de más"
            margin
            valuePropName="checked"
            tooltip="Al cierre de mes, cobra al empleado el ISR que se le retuvo de más durante el mes. Si está apagado, ese ajuste nunca se aplica."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="ajustarSubsCausadoMenos"
            label="Ajustar subsidio causado de menos"
            margin
            valuePropName="checked"
            tooltip="Al cierre de mes, incluye el subsidio al empleo causado de menos dentro del ajuste de ISR del mes."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="aplicarSubsidioArt174"
            label="Restar subsidio en la tasa del Art. 174"
            margin
            valuePropName="checked"
            tooltip="Resta el subsidio al empleo en las dos corridas de tarifa que forman la tasa efectiva del Art. 174 RLISR (aguinaldo, PTU, prima dominical y prima vacacional). Déjalo apagado salvo que necesites empatar importes contra un NOMIPAQ que traiga esta bandera activa: el Art. 174 fracc. II sólo menciona la tarifa del Art. 96."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="netearSubsEntregado"
            label="Netear subsidio entregado"
            margin
            valuePropName="checked"
            tooltip="Cambia cómo se calcula el subsidio entregado que no correspondía al cierre de mes: en vez de tomar el mínimo entre el subsidio causado y el causado ajustado, usa 0 cuando el causado ajustado es prácticamente nulo."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="educacion1Pct"
            label="Aplicar 1% de impuesto estatal de educación"
            margin
            valuePropName="checked"
            tooltip="Activa el cálculo de un impuesto estatal adicional del 1% sobre la base gravable + exenta de ISR (aplica solo en los estados donde este impuesto exista)."
          >
            <SwitchItem />
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>Ajuste de Neto</Title>

          <FormItem
            name="multiploAjusteNeto"
            label="Múltiplo de redondeo del neto"
            margin
            tooltip="El neto a pagar (más el residuo de redondeo acumulado del empleado) se redondea al múltiplo más cercano de este valor. Déjalo en 0 para desactivar el redondeo."
          >
            <InputNumberItem min={0} step={0.05} style={{ width: "100%" }} />
          </FormItem>

          <FormItem
            name="preferenciaAjusteNetoNegativo"
            label="Preferir ajuste negativo en empate"
            margin
            valuePropName="checked"
            tooltip="Cuando el residuo cae exactamente a la mitad del múltiplo, decide hacia dónde redondear: encendido = redondea hacia arriba (favorece al empleado); apagado = redondea hacia abajo."
          >
            <SwitchItem />
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>IMSS y Salarios Mínimos</Title>

          <FormItem
            name="primaRiesgoTrabajo"
            label="Prima de riesgo de trabajo"
            margin
            tooltip="Fracción decimal (0.005 = 0.5%, clase I mínima). Se usa como respaldo para calcular el riesgo de trabajo solo cuando el Registro Patronal del empleado no tiene una prima vigente ni clase de riesgo asignada."
          >
            <InputNumberItem min={0} max={1} step={0.001} style={{ width: "100%" }} />
          </FormItem>

          <FormItem
            name="zonaMinimo"
            label="Zona de salarios mínimos"
            margin
            tooltip="Zona geográfica usada para consultar la tabla de Salarios Mínimos vigente al calcular exenciones de ISR y riesgo de trabajo."
          >
            <SelectItem>
              <Option value="A">Zona A</Option>
              <Option value="B">Zona B</Option>
              <Option value="C">Zona C</Option>
            </SelectItem>
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>Proporcionales de Prestaciones</Title>

          <FormItem
            name="diasAnioAguinaldo"
            label="Días del año para aguinaldo"
            margin
            tooltip="Divisor del aguinaldo proporcional: días de aguinaldo de la tabla de prestaciones ÷ este número × días trabajados en el ejercicio. Afecta tanto el aguinaldo masivo de diciembre como el del finiquito. 365 = año natural; 364 = 52 semanas exactas. Cambiarlo mueve el importe: 15 días sobre 215 trabajados dan $2,886.24 con 365 y $2,894.17 con 364."
          >
            <SelectItem>
              <Option value={360}>360 (12 meses de 30 días)</Option>
              <Option value={364}>364 (52 semanas)</Option>
              <Option value={365}>365 (año natural)</Option>
            </SelectItem>
          </FormItem>

          <FormItem
            name="diasAnioVacaciones"
            label="Días del año para vacaciones"
            margin
            tooltip="Divisor de las vacaciones proporcionales del finiquito: días de vacaciones por antigüedad ÷ este número × días trabajados en el ejercicio. La prima vacacional no tiene su propio divisor, se calcula como un porcentaje de estos días. No tiene que coincidir con el del aguinaldo: CONTPAQi los maneja por separado."
          >
            <SelectItem>
              <Option value={360}>360 (12 meses de 30 días)</Option>
              <Option value={364}>364 (52 semanas)</Option>
              <Option value={365}>365 (año natural)</Option>
            </SelectItem>
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>Antigüedad para la tabla de prestaciones</Title>

          {/* Apagados = "años cumplidos + 1", que es como se calculó siempre. Prendidos = años
              cumplidos, con el primer año en curso contando como 1. Uno por cálculo para que
              la empresa pueda mover uno sin mover los otros. */}
          <FormItem
            name="antiguedadCumplidosSbc"
            label="SBC por años cumplidos"
            margin
            valuePropName="checked"
            tooltip="Antigüedad con la que se busca el factor de integración en la tabla de prestaciones: F10 de Agregar/Editar Empleado, la revisión y el aviso de SBC desfasado, la corrección en bloque y el Aumento de Sueldo. Apagado: años cumplidos + 1 (con 1 año 6 meses toma la fila 2). Encendido: años cumplidos, y el primer año en curso cuenta como 1 (con 1 año 6 meses toma la fila 1)."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="antiguedadCumplidosAguinaldo"
            label="Aguinaldo por años cumplidos"
            margin
            valuePropName="checked"
            tooltip="Antigüedad con la que se buscan los días de aguinaldo en la tabla de prestaciones, en el asistente de aguinaldo y en el finiquito. Apagado: años cumplidos + 1 a la fecha de corte. Encendido: años cumplidos, y el primer año en curso cuenta como 1. Cambiarlo mueve el importe del aguinaldo."
          >
            <SwitchItem />
          </FormItem>

          <FormItem
            name="antiguedadCumplidosVacacionesFiniquito"
            label="Vacaciones del finiquito por años cumplidos"
            margin
            valuePropName="checked"
            tooltip="Antigüedad con la que el finiquito busca los días de vacaciones que se están devengando. Apagado: años cumplidos + 1 (con 1 año 6 meses toma los días de la fila 2). Encendido: años cumplidos, y el primer año en curso cuenta como 1 (con 1 año 6 meses toma los de la fila 1). El kárdex de vacaciones, la prima vacacional y la prima de antigüedad no cambian: ya usan años cumplidos."
          >
            <SwitchItem />
          </FormItem>

          <Divider style={{ margin: "8px 0 16px" }} />
          <Title level={5}>Fonacot</Title>

          <FormItem
            name="centroTrabajoFonacot"
            label="Centro de Trabajo Fonacot"
            margin
            tooltip="Código de centro de trabajo asignado por FONACOT. Solo de referencia, no afecta el cálculo de nómina."
          >
            <InputItem />
          </FormItem>

          <FormItem
            name="numeroFonacot"
            label="Número Fonacot"
            margin
            tooltip="Número de empresa/patrón asignado por FONACOT. Solo de referencia, no afecta el cálculo de nómina."
          >
            <InputItem />
          </FormItem>
        </Form>
      </div>
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ConfiguracionNominaContabilidad);
